/**
 * Token storage helpers. JWT payload is decoded for UI hints only —
 * the server is the authoritative source of truth on every request.
 */

const ACCESS_KEY = 'wehi_access';
/** Exported so a cross-tab `storage` listener can recognise its own key. */
export const ACCESS_TOKEN_KEY = ACCESS_KEY;
const REFRESH_KEY = 'wehi_refresh';

export interface JwtPayload {
  sub: string;
  email: string;
  name?: string;
  /** Active organization id — the token is always scoped to exactly one. */
  org: string;
  org_slug: string;
  /** Role INSIDE that organization. */
  role: string;
  platform_admin?: boolean;
  iat?: number;
  exp?: number;
}

export interface SessionUser {
  id: string;
  email: string;
  name: string;
  role: string;
  org?: string;
  org_slug?: string;
  platform_admin?: boolean;
}

function currentStore(): Storage {
  // Where the tokens already live. When "remember me" is unchecked they sit in
  // sessionStorage (cleared when the tab closes); default to localStorage.
  try {
    if (
      sessionStorage.getItem(ACCESS_KEY) !== null ||
      sessionStorage.getItem(REFRESH_KEY) !== null
    ) {
      return sessionStorage;
    }
  } catch {
    // ignore
  }
  return localStorage;
}

/**
 * Fired whenever the stored access token starts naming a DIFFERENT organization.
 *
 * The access token is the only thing that decides which tenant a request hits,
 * and it is swapped in four places: login, signup, switch-org, and the silent
 * refresh in lib/api.ts. Three of them empty the query cache; the refresh path
 * did not — and /auth/refresh mints a token for whatever `active_org_id` the
 * server session holds, which is not always the org the UI is displaying. That
 * is the same "showing a neighbour's answer" bug as before, in the one path
 * the original fix did not cover.
 *
 * Rather than remember to clear in a fifth place later, every swap goes through
 * here and anything that changes tenant is announced once.
 */
type TenantChangeListener = (nextOrg: string | null, prevOrg: string | null) => void;

let tenantChangeListener: TenantChangeListener | null = null;

export function onTenantChange(fn: TenantChangeListener): () => void {
  tenantChangeListener = fn;
  return () => {
    if (tenantChangeListener === fn) tenantChangeListener = null;
  };
}

/** The organization an access token is scoped to, without verifying it. */
export function orgOf(token: string | null): string | null {
  return decodeUser(token)?.org ?? null;
}

export function setTokens(
  access: string,
  refresh?: string | null,
  remember?: boolean,
  /** Set by callers that already clear the cache themselves (login, switch-org). */
  opts?: { silent?: boolean },
): void {
  const prevOrg = orgOf(getAccess());
  try {
    let store: Storage;
    if (remember === undefined) {
      // Rotation / refresh — keep tokens in whichever store they already occupy.
      store = currentStore();
    } else {
      store = remember ? localStorage : sessionStorage;
      const other = remember ? sessionStorage : localStorage;
      other.removeItem(ACCESS_KEY);
      other.removeItem(REFRESH_KEY);
    }
    store.setItem(ACCESS_KEY, access);
    if (refresh) {
      store.setItem(REFRESH_KEY, refresh);
    }
  } catch {
    // storage unavailable; ignore.
  }

  const nextOrg = orgOf(access);
  if (!opts?.silent && nextOrg !== prevOrg) {
    tenantChangeListener?.(nextOrg, prevOrg);
  }
}

export function getAccess(): string | null {
  try {
    return sessionStorage.getItem(ACCESS_KEY) ?? localStorage.getItem(ACCESS_KEY);
  } catch {
    return null;
  }
}

export function getRefresh(): string | null {
  try {
    return sessionStorage.getItem(REFRESH_KEY) ?? localStorage.getItem(REFRESH_KEY);
  } catch {
    return null;
  }
}

export function clearTokens(): void {
  try {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
    sessionStorage.removeItem(ACCESS_KEY);
    sessionStorage.removeItem(REFRESH_KEY);
  } catch {
    // ignore
  }
}

/**
 * Pulls the JWT payload without verifying. Server is authoritative —
 * this is purely for client-side UI hints (e.g. show role chip while
 * the /me call is in flight).
 */
export function decodeUser(token: string | null): SessionUser | null {
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    const payload = parts[1];
    // base64url -> base64
    const b64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const json = atob(padded);
    const decoded = JSON.parse(json) as JwtPayload;
    return {
      id: decoded.sub,
      email: decoded.email,
      name: decoded.name ?? decoded.email,
      role: decoded.role,
      org: decoded.org,
      org_slug: decoded.org_slug,
      platform_admin: decoded.platform_admin,
    };
  } catch {
    return null;
  }
}
